import type { Recipe } from '@/types'

import { useEffect, useRef } from 'react'
import { ExternalLink, Heart } from 'lucide-react'
import {
  Card,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

export interface RecipeSearchCardProps {
  recipe: Recipe
  selected: boolean
  alreadyInMix: boolean
  assignable?: boolean
  onToggle: () => void
}

function getDomain(url?: string | null): string {
  if (!url) return ''
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

export default function RecipeSearchCard({
  recipe,
  selected,
  alreadyInMix,
  assignable = false,
  onToggle,
}: RecipeSearchCardProps) {
  const domain = getDomain(recipe.url)
  const countRef = useRef<HTMLSpanElement>(null)
  const prevCount = useRef<number>(recipe.favoriteCount ?? 0)

  useEffect(() => {
    const next = recipe.favoriteCount ?? 0
    const prev = prevCount.current
    if (countRef.current && next > prev) {
      countRef.current.classList.add('animate-count-bump')
      countRef.current.addEventListener(
        'animationend',
        () => countRef.current?.classList.remove('animate-count-bump'),
        { once: true }
      )
    }
    prevCount.current = next
  }, [recipe.favoriteCount])

  return (
    <Card
      onClick={() => {
        if (!alreadyInMix || assignable) {
          onToggle()
        }
      }}
      className={`
        relative group overflow-hidden rounded-lg border transition-all duration-200
        ${alreadyInMix && !assignable
          ? 'opacity-60 border-muted cursor-not-allowed'
          : selected
            ? 'border-primary ring-2 ring-primary/40 bg-primary/5 cursor-pointer'
            : 'border-border cursor-pointer hover-supported:hover:shadow-md hover-supported:hover:scale-[1.02] hover-supported:hover:z-10 hover-supported:hover:border-primary/50'
        }
      `}
    >
      {/* Already in mix badge */}
      {alreadyInMix && !assignable && (
        <div className="absolute top-3 left-3 z-10 bg-muted text-muted-foreground text-xs px-2 py-1 rounded-full">
          Already in mix
        </div>
      )}

      {/* Assign badge (when recipe is in mix but can be reassigned to a slot) */}
      {alreadyInMix && assignable && (
        <div className="absolute top-3 left-3 z-10 bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">
          Assign
        </div>
      )}

      {/* Selected indicator */}
      {selected && !alreadyInMix && (
        <div className="absolute top-3 right-3 z-10 bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
          Selected
        </div>
      )}

      {/* Image area */}
      <div className="h-32 w-full bg-muted overflow-hidden">
        {recipe.image ? (
          <img
            src={recipe.image}
            alt={recipe.name}
            className="h-full w-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
              const parent = target.parentElement
              if (parent) {
                parent.innerHTML = `<div class="h-full w-full flex items-center justify-center"><span class="text-muted-foreground text-sm">🍳 Recipe</span></div>`
              }
            }}
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <span className="text-muted-foreground text-sm">🍳 Recipe</span>
          </div>
        )}
      </div>

      {/* Heart + count (left) + ingredients pill (right) */}
      <div className="px-3 pt-2 flex items-center justify-between text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <Heart className="h-4 w-4" />
          <span ref={countRef} className="text-xs font-medium">{recipe.favoriteCount ?? 0}</span>
        </div>
        <span className="inline-flex items-center bg-secondary text-secondary-foreground rounded-full px-2 py-0.5 text-xs font-medium">
          {recipe.ingredientIds?.length || 0} ingredient{recipe.ingredientIds?.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Card content */}
      <CardHeader className="p-3">
        <CardTitle className="text-base leading-tight truncate">
          {recipe.name}
        </CardTitle>

        {/* URL pill — stopPropagation so parent card click doesn&apos;t toggle */}
        {domain && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              if (recipe.url) {
                window.open(recipe.url, '_blank', 'noopener,noreferrer')
              }
            }}
            className="w-fit inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:text-foreground hover:bg-muted cursor-pointer"
            title={recipe.url}
          >
            <ExternalLink className="h-3 w-3" />
            {domain}
          </button>
        )}
      </CardHeader>
    </Card>
  )
}
